import {
  AlertCircle,
  ArrowUpDown,
  Check,
  CheckSquare,
  ChevronRight,
  Download,
  FileDown,
  Loader2,
  Search,
  Square,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import {
  base64ToBlob,
  downloadStackQuestionsDocx,
  exportBatchStackQuestionsDocx,
  triggerBlobDownload,
  useStackOverview,
  useTaxonomy,
  type StackExportBatchOut,
  type StackSummaryOut,
} from "@/api/queries";
import { useCan } from "@/auth/member";
import { Button } from "@/components/ui/button";
import { Combobox, FieldLabel, Input } from "@/components/ui/input";
import { Empty, ErrorNote, Panel, PanelHeader } from "@/components/ui/panel";
import { SkeletonStat, SkeletonTableRow } from "@/components/ui/skeleton";
import { Stat } from "@/components/ui/stat";
import { cn, formatCount, formatScore } from "@/lib/utils";

const TH = "text-bank-muted px-3 py-2 font-mono text-[10.5px] font-medium tracking-[0.07em] uppercase select-none";

type SortField = "total_questions" | "approval_rate" | "duplicate_rate" | "rejection_rate" | "avg_quality_score" | "stack_path";

function HealthChip({ status }: { status: string }) {
  switch (status) {
    case "healthy":
      return (
        <span
          title="Health: Healthy (Quality and review pipeline normal)"
          aria-label="Health: Healthy"
          className="bg-bank-good-wash text-bank-good-ink inline-flex size-5 items-center justify-center rounded-full transition-transform hover:scale-110"
        >
          <span className="bg-bank-good size-2 rounded-full" aria-hidden="true" />
        </span>
      );
    case "needs_review":
      return (
        <span
          title="Health: Needs Review (Pending or stale questions awaiting review)"
          aria-label="Health: Needs Review"
          className="bg-bank-attention-wash text-bank-attention-ink inline-flex size-5 items-center justify-center rounded-full transition-transform hover:scale-110"
        >
          <span className="bg-bank-attention size-2 rounded-full" aria-hidden="true" />
        </span>
      );
    case "high_duplicate":
      return (
        <span
          title="Health: High Duplicate (>25% duplicate rate)"
          aria-label="Health: High Duplicate"
          className="bg-bank-dup-wash text-bank-dup-ink inline-flex size-5 items-center justify-center rounded-full transition-transform hover:scale-110"
        >
          <span className="bg-bank-dup size-2 rounded-full" aria-hidden="true" />
        </span>
      );
    case "high_rejection":
      return (
        <span
          title="Health: High Rejection (>25% rejection rate)"
          aria-label="Health: High Rejection"
          className="bg-bank-bad-wash text-bank-bad-ink inline-flex size-5 items-center justify-center rounded-full transition-transform hover:scale-110"
        >
          <span className="bg-bank-bad size-2 rounded-full" aria-hidden="true" />
        </span>
      );
    case "sparse":
      return (
        <span
          title="Health: Sparse (<10 questions in bank)"
          aria-label="Health: Sparse"
          className="border-bank-border bg-bank-surface-2 text-bank-muted inline-flex size-5 items-center justify-center rounded-full border transition-transform hover:scale-110"
        >
          <span className="bg-bank-muted size-2 rounded-full" aria-hidden="true" />
        </span>
      );
    default:
      return (
        <span
          title="Health: No Data (0 questions)"
          aria-label="Health: No Data"
          className="bg-bank-inert-wash text-bank-inert-ink inline-flex size-5 items-center justify-center rounded-full transition-transform hover:scale-110"
        >
          <span className="bg-bank-inert size-2 rounded-full" aria-hidden="true" />
        </span>
      );
  }
}

function StackDifficultyBar({ item }: { item: StackSummaryOut }) {
  const diff = (item.difficulty_counts ?? {}) as Record<number | string, number>;
  const d1 = Number(diff[1] ?? diff["1"] ?? 0);
  const d2 = Number(diff[2] ?? diff["2"] ?? 0);
  const d3 = Number(diff[3] ?? diff["3"] ?? 0);
  const d4 = Number(diff[4] ?? diff["4"] ?? 0);
  const d5 = Number(diff[5] ?? diff["5"] ?? 0);

  const total = item.total_questions;
  const maxCount = Math.max(d1, d2, d3, d4, d5, 1);

  const levels = [
    { lvl: "L1", label: "Foundational", count: d1, color: "bg-emerald-500" },
    { lvl: "L2", label: "Working", count: d2, color: "bg-teal-400" },
    { lvl: "L3", label: "Proficient", count: d3, color: "bg-sky-400" },
    { lvl: "L4", label: "Advanced", count: d4, color: "bg-amber-400" },
    { lvl: "L5", label: "Mastery", count: d5, color: "bg-purple-400" },
  ];

  if (total === 0) {
    return (
      <div className="flex min-w-[190px] max-w-[210px] items-end justify-between gap-1.5 py-0.5 select-none">
        {levels.map((l) => (
          <div key={l.lvl} className="flex flex-col items-center gap-1">
            <div className="bg-bank-surface-3/40 h-6 w-3.5 rounded-xs" />
            <span className="font-mono text-[10px] text-bank-muted/30 tabular-nums">0</span>
          </div>
        ))}
      </div>
    );
  }

  const tooltip = `Difficulty Breakdown (${item.stack_path} · ${total} Qs):
• Level 1 (Foundational): ${d1} (${((d1 / total) * 100).toFixed(1)}%)
• Level 2 (Working): ${d2} (${((d2 / total) * 100).toFixed(1)}%)
• Level 3 (Proficient): ${d3} (${((d3 / total) * 100).toFixed(1)}%)
• Level 4 (Advanced): ${d4} (${((d4 / total) * 100).toFixed(1)}%)
• Level 5 (Mastery): ${d5} (${((d5 / total) * 100).toFixed(1)}%)`;

  return (
    <div
      className="flex min-w-[190px] max-w-[210px] items-end justify-between gap-1.5 py-0.5 select-none"
      title={tooltip}
    >
      {levels.map((l) => {
        const hasCount = l.count > 0;
        const heightPct = hasCount ? Math.max(Math.round((l.count / maxCount) * 100), 16) : 6;
        const pctOfTotal = total > 0 ? ((l.count / total) * 100).toFixed(1) : "0";

        return (
          <div
            key={l.lvl}
            title={`${l.lvl} ${l.label}: ${l.count} questions (${pctOfTotal}%)`}
            className="group/bar flex flex-col items-center gap-1 transition-transform hover:scale-105"
          >
            {/* Equalizer Vertical Track */}
            <div className="bg-bank-surface-3/50 flex h-6 w-3.5 items-end justify-center overflow-hidden rounded-xs p-[1px]">
              <div
                className={cn(
                  l.color,
                  "w-full rounded-t-xs transition-all duration-300 group-hover/bar:brightness-110",
                  !hasCount && "bg-bank-surface-3/60"
                )}
                style={{ height: `${heightPct}%` }}
              />
            </div>

            {/* Metric Value */}
            <span
              className={cn(
                "font-mono text-[10px] tabular-nums transition-colors",
                hasCount ? "font-semibold text-bank-ink" : "text-bank-muted/30 font-normal"
              )}
            >
              {l.count}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function StackExportModal({
  open,
  onClose,
  items,
  initialSelected,
}: {
  open: boolean;
  onClose: () => void;
  items: StackSummaryOut[];
  initialSelected?: string[] | undefined;
}) {
  const [selectedStacks, setSelectedStacks] = useState<Set<string>>(() => {
    return initialSelected && initialSelected.length > 0
      ? new Set(initialSelected)
      : new Set();
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [exportResult, setExportResult] = useState<StackExportBatchOut | null>(null);
  const [downloadedBlobs, setDownloadedBlobs] = useState<Record<string, { blob: Blob; filename: string }>>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      if (initialSelected && initialSelected.length > 0) {
        setSelectedStacks(new Set(initialSelected));
      }
      setSearchQuery("");
      setExportResult(null);
      setErrorMessage(null);
    }
  }, [open, initialSelected]);

  useEffect(() => {
    if (!open) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape" && !isExporting) {
        onClose();
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose, isExporting]);

  const filteredItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (st) =>
        st.stack_path.toLowerCase().includes(q) ||
        st.bucket.toLowerCase().includes(q),
    );
  }, [items, searchQuery]);

  const selectedItems = useMemo(() => {
    return items.filter((st) => selectedStacks.has(st.stack_path));
  }, [items, selectedStacks]);

  const totalSelectedApproved = useMemo(() => {
    return selectedItems.reduce((acc, st) => acc + st.approved_count, 0);
  }, [selectedItems]);

  const activeStacksCount = useMemo(() => {
    return items.filter((st) => st.approved_count > 0).length;
  }, [items]);

  function handleSelectAllActive() {
    const active = items.filter((st) => st.approved_count > 0).map((st) => st.stack_path);
    setSelectedStacks(new Set(active));
  }

  function handleClearAll() {
    setSelectedStacks(new Set());
  }

  function toggleStack(stackPath: string) {
    setSelectedStacks((prev) => {
      const next = new Set(prev);
      if (next.has(stackPath)) {
        next.delete(stackPath);
      } else {
        next.add(stackPath);
      }
      return next;
    });
  }

  async function handleStartExport() {
    if (selectedStacks.size === 0 || isExporting) return;
    setIsExporting(true);
    setErrorMessage(null);
    setExportResult(null);

    try {
      const targetIds = Array.from(selectedStacks);
      const result = await exportBatchStackQuestionsDocx(targetIds);
      setExportResult(result);

      // Convert base64 to blobs and trigger separate downloads sequentially
      const newBlobs: Record<string, { blob: Blob; filename: string }> = {};
      const readyItems = result.items.filter(
        (it) => it.status === "ready" && it.content_base64 && it.filename,
      );

      for (let i = 0; i < readyItems.length; i++) {
        const item = readyItems[i];
        if (!item || !item.content_base64 || !item.filename) continue;
        const blob = base64ToBlob(
          item.content_base64,
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        );
        newBlobs[item.stack_path] = { blob, filename: item.filename };

        // Trigger separate download
        triggerBlobDownload(blob, item.filename);

        // 350ms delay between download triggers so modern browsers register each file separately
        if (i < readyItems.length - 1) {
          await new Promise((res) => setTimeout(res, 350));
        }
      }

      setDownloadedBlobs(newBlobs);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to execute batch export";
      setErrorMessage(msg);
    } finally {
      setIsExporting(false);
    }
  }

  function handleReDownload(stackPath: string) {
    const item = downloadedBlobs[stackPath];
    if (item) {
      triggerBlobDownload(item.blob, item.filename);
    }
  }

  if (!open) return null;

  return (
    <>
      <button
        type="button"
        aria-label="Close export dialog"
        onClick={onClose}
        disabled={isExporting}
        className="fixed inset-0 z-30 cursor-default bg-black/40 backdrop-blur-[1px]"
      />
      <div
        role="dialog"
        aria-modal="true"
        className="border-bank-border bg-bank-surface fixed top-1/2 left-1/2 z-40 flex max-h-[90vh] w-full max-w-xl -translate-x-1/2 -translate-y-1/2 flex-col rounded-xl border shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="border-bank-border bg-bank-surface-2 flex items-center justify-between border-b px-4 py-3 shrink-0">
          <div className="flex items-center gap-2">
            <span className="bg-bank-accent-wash text-bank-accent-ink rounded-md p-1.5">
              <FileDown className="size-4" />
            </span>
            <div>
              <h2 className="text-sm font-semibold">Export Approved Questions</h2>
              <p className="text-bank-muted font-mono text-[11px]">
                Microsoft Word (.docx) · Question text only · Individual file per technology
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isExporting}
            aria-label="Close"
            className="text-bank-muted hover:bg-bank-surface-3 rounded-md p-1 transition-colors disabled:opacity-50"
          >
            <X className="size-4" />
          </button>
        </div>

        {/* Body content */}
        <div className="space-y-4 p-4 text-sm overflow-y-auto flex-1">
          {errorMessage && (
            <div className="border-bank-bad bg-bank-bad-wash text-bank-bad-ink flex items-center gap-2 rounded-lg border p-2.5 text-xs">
              <AlertCircle className="size-4 shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          {exportResult ? (
            /* Post-Export Result Summary View */
            <div className="space-y-4">
              <div className="border-bank-good bg-bank-good-wash text-bank-good-ink rounded-lg border p-3">
                <div className="flex items-center gap-2 font-semibold text-xs">
                  <Check className="size-4 text-bank-good shrink-0" />
                  <span>Export Completed Successfully</span>
                </div>
                <p className="text-bank-muted mt-1 text-xs">
                  {exportResult.total_exported} individual DOCX file{exportResult.total_exported !== 1 ? "s" : ""} generated and downloaded.
                  {exportResult.total_empty > 0 && ` ${exportResult.total_empty} technology stack${exportResult.total_empty !== 1 ? "s were" : " was"} skipped (0 approved questions).`}
                </p>
              </div>

              <div className="space-y-2">
                <FieldLabel>Generated Files Manifest</FieldLabel>
                <div className="border-bank-border bg-bank-surface-2 divide-bank-border divide-y rounded-lg border overflow-hidden">
                  {exportResult.items.map((item) => (
                    <div key={item.stack_path} className="flex items-center justify-between p-2.5 text-xs">
                      <div className="flex items-center gap-2 min-w-0">
                        {item.status === "ready" ? (
                          <Check className="size-3.5 text-bank-good shrink-0" />
                        ) : item.status === "no_approved_questions" ? (
                          <AlertCircle className="size-3.5 text-bank-attention shrink-0" />
                        ) : (
                          <X className="size-3.5 text-bank-bad shrink-0" />
                        )}
                        <div className="min-w-0">
                          <p className="font-mono font-medium truncate">
                            {item.filename ?? item.stack_path}
                          </p>
                          <p className="text-bank-muted text-[11px]">
                            {item.status === "ready"
                              ? `${item.count} approved questions`
                              : item.status === "no_approved_questions"
                                ? "0 approved questions (skipped)"
                                : item.error_message}
                          </p>
                        </div>
                      </div>
                      {item.status === "ready" && downloadedBlobs[item.stack_path] && (
                        <button
                          type="button"
                          onClick={() => handleReDownload(item.stack_path)}
                          className="text-bank-muted hover:text-bank-accent-ink hover:bg-bank-surface-3 inline-flex items-center gap-1 rounded border border-bank-border px-2 py-1 text-[11px] font-mono transition-colors shrink-0"
                          title="Re-download this specific file"
                        >
                          <Download className="size-3" /> Re-download
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            /* Multi-Select Technology Picker View */
            <>
              {/* Search & Bulk Controls */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <FieldLabel>Select Technologies / Stacks to Export</FieldLabel>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleSelectAllActive}
                      className="text-bank-accent-ink hover:underline text-[11px] font-mono"
                    >
                      Select All Active ({activeStacksCount})
                    </button>
                    <span className="text-bank-muted text-xs">·</span>
                    <button
                      type="button"
                      onClick={handleClearAll}
                      className="text-bank-muted hover:text-bank-ink hover:underline text-[11px] font-mono"
                    >
                      Clear
                    </button>
                  </div>
                </div>

                <div className="relative">
                  <Search className="text-bank-muted pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2" />
                  <Input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search technologies (e.g. javascript, dotnet, sql)..."
                    className="pl-8"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery("")}
                      className="text-bank-muted hover:text-bank-ink absolute top-1/2 right-2.5 -translate-y-1/2"
                    >
                      <X className="size-3.5" />
                    </button>
                  )}
                </div>
              </div>

              {/* Technologies List */}
              <div className="border-bank-border bg-bank-surface-2 divide-bank-border max-h-52 divide-y rounded-lg border overflow-y-auto">
                {filteredItems.length === 0 ? (
                  <div className="text-bank-muted p-4 text-center text-xs">
                    No technology stacks match &ldquo;{searchQuery}&rdquo;
                  </div>
                ) : (
                  filteredItems.map((st) => {
                    const isSelected = selectedStacks.has(st.stack_path);
                    const hasApproved = st.approved_count > 0;
                    return (
                      <div
                        key={st.stack_path}
                        role="checkbox"
                        aria-checked={isSelected}
                        tabIndex={0}
                        onClick={() => toggleStack(st.stack_path)}
                        onKeyDown={(e) => {
                          if (e.key === " " || e.key === "Enter") {
                            e.preventDefault();
                            toggleStack(st.stack_path);
                          }
                        }}
                        className={cn(
                          "flex cursor-pointer items-center justify-between p-2.5 text-xs transition-colors select-none rounded-sm focus-visible:outline-bank-accent focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-1",
                          isSelected
                            ? "bg-bank-accent-wash/40 hover:bg-bank-accent-wash/60"
                            : "hover:bg-bank-surface-3",
                          !hasApproved && "opacity-60",
                        )}
                      >
                        <div className="flex items-center gap-2.5">
                          <button
                            type="button"
                            tabIndex={-1}
                            aria-label={`Toggle ${st.stack_path}`}
                            className="text-bank-accent shrink-0"
                          >
                            {isSelected ? (
                              <CheckSquare className="size-4 text-bank-accent" />
                            ) : (
                              <Square className="size-4 text-bank-muted" />
                            )}
                          </button>
                          <span className="font-mono font-medium">{st.stack_path}</span>
                          <span className="bg-bank-surface text-bank-muted rounded border border-bank-border px-1.5 py-0.2 font-mono text-[10px]">
                            {st.bucket}
                          </span>
                        </div>
                        <div>
                          {hasApproved ? (
                            <span className="bg-bank-good-wash text-bank-good-ink rounded px-2 py-0.5 font-mono text-[11px] font-semibold tabular-nums">
                              {st.approved_count} approved
                            </span>
                          ) : (
                            <span className="bg-bank-surface text-bank-muted rounded border border-bank-border px-2 py-0.5 font-mono text-[10.5px]">
                              0 approved · skipped
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Selection Summary and File Preview */}
              {selectedStacks.size > 0 ? (
                <div className="border-bank-border bg-bank-surface-2 space-y-2 rounded-lg border p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-bank-ink font-mono text-xs font-semibold">
                      {selectedStacks.size} technolog{selectedStacks.size !== 1 ? "ies" : "y"} selected
                    </span>
                    <span className="bg-bank-good-wash text-bank-good-ink rounded px-2 py-0.5 font-mono text-xs font-bold tabular-nums">
                      {totalSelectedApproved} total questions
                    </span>
                  </div>
                  <div className="space-y-1">
                    <span className="text-bank-muted font-mono text-[10.5px] uppercase tracking-wider block">
                      Files to generate (one independent DOCX per technology):
                    </span>
                    <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                      {selectedItems.map((st) => (
                        <span
                          key={st.stack_path}
                          className={cn(
                            "inline-flex items-center gap-1 rounded border px-2 py-0.5 font-mono text-[11px]",
                            st.approved_count > 0
                              ? "border-bank-border bg-bank-surface text-bank-ink"
                              : "border-bank-border/50 bg-bank-surface/50 text-bank-muted line-through",
                          )}
                        >
                          approved-{st.stack_path.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}-questions.docx
                          {st.approved_count > 0 ? (
                            <span className="text-bank-good font-semibold">({st.approved_count})</span>
                          ) : (
                            <span className="text-bank-attention font-semibold">(0 · skipped)</span>
                          )}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="border-bank-border bg-bank-surface-2 text-bank-muted rounded-lg border p-3 text-center text-xs">
                  No technologies selected yet. Select one or more technologies above to export.
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer controls */}
        <div className="border-bank-border bg-bank-surface-2 flex items-center justify-between border-t px-4 py-2.5 shrink-0">
          <div className="text-bank-muted font-mono text-xs">
            {!exportResult && selectedStacks.size > 0 && (
              <span>{selectedStacks.size} selected</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={onClose} disabled={isExporting}>
              {exportResult ? "Done" : "Cancel"}
            </Button>
            {exportResult ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setExportResult(null);
                }}
              >
                Export Other Stacks
              </Button>
            ) : (
              <Button
                size="sm"
                variant="primary"
                disabled={selectedStacks.size === 0 || isExporting}
                onClick={() => {
                  void handleStartExport();
                }}
              >
                {isExporting ? (
                  <>
                    <Loader2 className="size-3.5 animate-spin mr-1" /> Generating DOCX Files…
                  </>
                ) : selectedStacks.size === 0 ? (
                  "Select at least 1 technology"
                ) : (
                  <>
                    <FileDown className="size-3.5 mr-1" />
                    Download {selectedStacks.size} DOCX File{selectedStacks.size !== 1 ? "s" : ""}
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default function StackOverview() {
  const navigate = useNavigate();
  const canExport = useCan("export:questions_docx");
  const [search, setSearch] = useState("");
  const [bucket, setBucket] = useState("");
  const [healthFilter, setHealthFilter] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [sortField, setSortField] = useState<SortField>("total_questions");
  const [sortAsc, setSortAsc] = useState(false);

  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportModalInitial, setExportModalInitial] = useState<string[] | undefined>(undefined);
  const [exportingStack, setExportingStack] = useState<string | null>(null);
  const [exportFeedback, setExportFeedback] = useState<{ type: "success" | "error"; message: string } | null>(null);

  const { data, isLoading, error } = useStackOverview({
    bucket: bucket || undefined,
    includeInactive,
  });
  const { data: buckets } = useTaxonomy("bucket");

  function handleSort(field: SortField) {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(field === "stack_path");
    }
  }

  const items = useMemo(() => {
    let list = data?.items ?? [];

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((i) => i.stack_path.toLowerCase().includes(q));
    }

    if (healthFilter) {
      list = list.filter((i) => i.health_status === healthFilter);
    }

    return [...list].sort((a, b) => {
      let valA = a[sortField];
      let valB = b[sortField];

      if (valA === null || valA === undefined) valA = -1;
      if (valB === null || valB === undefined) valB = -1;

      if (typeof valA === "string" && typeof valB === "string") {
        return sortAsc ? valA.localeCompare(valB) : valB.localeCompare(valA);
      }

      const numA = Number(valA);
      const numB = Number(valB);
      return sortAsc ? numA - numB : numB - numA;
    });
  }, [data?.items, search, healthFilter, sortField, sortAsc]);

  async function handleExport(stackPath: string) {
    if (!stackPath || exportingStack) return;
    setExportingStack(stackPath);
    setExportFeedback(null);
    try {
      const filename = await downloadStackQuestionsDocx(stackPath);
      setExportFeedback({
        type: "success",
        message: `Successfully downloaded ${filename}`,
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to export DOCX";
      setExportFeedback({
        type: "error",
        message: msg,
      });
    } finally {
      setExportingStack(null);
    }
  }

  return (
    <div className="flex flex-col gap-4 p-4 pb-12">
      {/* Header */}
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="text-base font-semibold tracking-tight">Stack Overview</h1>
            <span className="bg-bank-accent-wash text-bank-accent-ink rounded px-2 py-0.5 font-mono text-xs font-medium">
              Portfolio Intelligence
            </span>
          </div>
          <p className="text-bank-muted mt-0.5 text-xs">
            Consolidated inventory, status distributions, and quality health across all Question Bank technologies.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canExport && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                const matches = search.trim()
                  ? items.filter((i) => i.approved_count > 0).map((i) => i.stack_path)
                  : undefined;
                setExportModalInitial(matches);
                setExportModalOpen(true);
              }}
            >
              <FileDown className="size-3.5" />
              Export Approved DOCX
            </Button>
          )}
          <Link to="/questions">
            <Button size="sm" variant="ghost">
              Open Question Bank →
            </Button>
          </Link>
        </div>
      </header>

      {exportFeedback && (
        <div
          className={cn(
            "flex items-center justify-between rounded-md border px-3 py-2 text-xs",
            exportFeedback.type === "success"
              ? "border-bank-good bg-bank-good-wash text-bank-good-ink"
              : "border-bank-bad bg-bank-bad-wash text-bank-bad-ink",
          )}
        >
          <div className="flex items-center gap-2">
            {exportFeedback.type === "success" ? (
              <Check className="size-3.5 text-bank-good shrink-0" />
            ) : (
              <AlertCircle className="size-3.5 text-bank-bad shrink-0" />
            )}
            <span>{exportFeedback.message}</span>
          </div>
          <button
            type="button"
            onClick={() => setExportFeedback(null)}
            className="hover:opacity-75 p-0.5"
            aria-label="Dismiss feedback"
          >
            <X className="size-3.5" />
          </button>
        </div>
      )}

      <ErrorNote error={error} />

      {/* KPI Cards */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {isLoading ? (
          <>
            <SkeletonStat />
            <SkeletonStat />
            <SkeletonStat />
            <SkeletonStat />
            <SkeletonStat />
          </>
        ) : (
          <>
            <Stat
              label="Total Stacks"
              value={formatCount(data?.total_stacks)}
              hint={`${data?.active_stacks ?? 0} active in taxonomy`}
            />
            <Stat
              label="Total Questions"
              value={formatCount(data?.total_questions)}
              hint="across complete bank dataset"
            />
            <Stat
              label="Approved Pool"
              value={
                data?.total_questions
                  ? `${formatCount(data.total_approved)} (${Math.round((data.total_approved / data.total_questions) * 100)}%)`
                  : "0"
              }
              hint="ready for Engine 2 picking"
            />
            <Stat
              label="Needs Review"
              value={formatCount(data?.total_pending)}
              hint="pending_review + stale"
              flag={(data?.total_pending ?? 0) > 0}
            />
            <Stat
              label="Coverage Gaps"
              value={`${data?.zero_coverage_stacks_count ?? 0} Stacks`}
              hint="registered with 0 questions"
              flag={(data?.zero_coverage_stacks_count ?? 0) > 0}
            />
          </>
        )}
      </div>

      {/* Filter and Inventory Table */}
      <Panel className="flex flex-col">
        <PanelHeader
          title="Technology Inventory"
          actions={
            isLoading ? (
              <div className="flex items-center gap-1.5 text-bank-muted font-mono text-xs">
                <Loader2 className="size-3.5 animate-spin" />
                <span>Loading stacks…</span>
              </div>
            ) : (
              <span className="text-bank-muted font-mono text-xs tabular-nums">
                {`${items.length} of ${data?.total_stacks ?? 0} stacks`}
              </span>
            )
          }
        />

        {/* Filter Toolbar */}
        <div className="border-bank-border bg-bank-surface-2 border-b p-3">
          <div className="grid gap-3 sm:grid-cols-[2fr_1fr_1fr_auto]">
            <div>
              <FieldLabel htmlFor="stack-search">Filter by technology</FieldLabel>
              <div className="relative">
                <Input
                  id="stack-search"
                  value={search}
                  placeholder="javascript, dotnet, hrms, aws…"
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            <div>
              <FieldLabel htmlFor="stack-bucket">Bucket</FieldLabel>
              <Combobox
                id="stack-bucket"
                value={bucket}
                onChange={setBucket}
                options={[
                  { value: "", label: "all buckets" },
                  ...(buckets ?? []).map((b) => ({ value: b.path, label: b.path })),
                ]}
              />
            </div>

            <div>
              <FieldLabel htmlFor="stack-health">Health condition</FieldLabel>
              <Combobox
                id="stack-health"
                value={healthFilter}
                onChange={setHealthFilter}
                options={[
                  { value: "", label: "all conditions" },
                  { value: "healthy", label: "Healthy" },
                  { value: "needs_review", label: "Needs Review" },
                  { value: "high_duplicate", label: "High Duplicate (>25%)" },
                  { value: "high_rejection", label: "High Rejection (>25%)" },
                  { value: "sparse", label: "Sparse (<10 Qs)" },
                  { value: "no_data", label: "No Data (0 Qs)" },
                ]}
              />
            </div>

            <div className="flex flex-wrap items-center gap-2 pb-1">
              <label className="text-bank-muted flex cursor-pointer items-center gap-2 py-1 text-xs">
                <input
                  type="checkbox"
                  checked={includeInactive}
                  onChange={(e) => setIncludeInactive(e.target.checked)}
                  className="rounded"
                />
                Include deactivated
              </label>
              {(search || bucket || healthFilter || includeInactive) && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setSearch("");
                    setBucket("");
                    setHealthFilter("");
                    setIncludeInactive(false);
                  }}
                  className="text-xs"
                >
                  Reset filters
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Main Stacks Table */}
        <div className="overflow-x-auto">
          {items.length === 0 && !isLoading ? (
            <Empty>No technologies match your filters.</Empty>
          ) : (
            <table className="w-full min-w-[860px] text-sm">
              <thead className="bg-bank-surface-2 border-bank-border sticky top-0 border-b text-left">
                <tr>
                  <th
                    className={cn(TH, "cursor-pointer hover:text-bank-ink")}
                    onClick={() => handleSort("stack_path")}
                  >
                    <span className="inline-flex items-center gap-1">
                      Technology / Stack <ArrowUpDown className="size-3" />
                    </span>
                  </th>
                  <th className={TH}>Bucket</th>
                  <th
                    className={cn(TH, "cursor-pointer text-right hover:text-bank-ink")}
                    onClick={() => handleSort("total_questions")}
                  >
                    <span className="inline-flex items-center justify-end gap-1">
                      Total <ArrowUpDown className="size-3" />
                    </span>
                  </th>
                  <th
                    className={cn(TH, "cursor-pointer text-right hover:text-bank-ink")}
                    onClick={() => handleSort("approval_rate")}
                  >
                    <span className="inline-flex items-center justify-end gap-1">
                      Approved <ArrowUpDown className="size-3" />
                    </span>
                  </th>
                  <th
                    className={cn(TH, "cursor-pointer text-right hover:text-bank-ink")}
                    onClick={() => handleSort("duplicate_rate")}
                  >
                    <span className="inline-flex items-center justify-end gap-1">
                      Duplicate <ArrowUpDown className="size-3" />
                    </span>
                  </th>
                  <th
                    className={cn(TH, "cursor-pointer text-right hover:text-bank-ink")}
                    onClick={() => handleSort("rejection_rate")}
                  >
                    <span className="inline-flex items-center justify-end gap-1">
                      Rejected <ArrowUpDown className="size-3" />
                    </span>
                  </th>
                  <th className={cn(TH, "text-right")}>Pending</th>
                  <th className={cn(TH, "min-w-[210px]")}>Difficulty Spread (L1–L5)</th>
                  <th
                    className={cn(TH, "cursor-pointer text-right hover:text-bank-ink")}
                    onClick={() => handleSort("avg_quality_score")}
                  >
                    <span className="inline-flex items-center justify-end gap-1">
                      Avg Qual <ArrowUpDown className="size-3" />
                    </span>
                  </th>
                  <th className={cn(TH, "w-12 text-center")} title="Stack Health Condition">Health</th>
                  <th className="px-3 py-2 text-right" />
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <SkeletonTableRow
                      key={i}
                      columns={[
                        "w-40",
                        "w-20",
                        "w-12 ml-auto",
                        "w-14 ml-auto",
                        "w-12 ml-auto",
                        "w-12 ml-auto",
                        "w-12 ml-auto",
                        "w-44",
                        "w-12 ml-auto",
                        "w-5 mx-auto",
                        "w-4 ml-auto",
                      ]}
                    />
                  ))
                ) : (
                  items.map((st) => (
                    <tr
                      key={st.stack_path}
                      onClick={() => {
                        void navigate(`/stack-overview/${encodeURIComponent(st.stack_path)}`);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          void navigate(`/stack-overview/${encodeURIComponent(st.stack_path)}`);
                        }
                      }}
                      tabIndex={0}
                      role="button"
                      aria-label={`View ${st.stack_path} stack diagnostics`}
                      className="border-bank-border hover:bg-bank-surface-2 focus-visible:outline-bank-accent cursor-pointer border-t transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2"
                    >
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs font-semibold">{st.stack_path}</span>
                          {st.is_deactivated && (
                            <span className="bg-bank-inert-wash text-bank-inert-ink rounded px-1.5 py-0.2 font-mono text-[10px]">
                              deactivated
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="text-bank-muted px-3 py-2.5 font-mono text-[11px]">
                        {st.bucket}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono text-[11px] font-medium tabular-nums">
                        {formatCount(st.total_questions)}
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono text-[11px] tabular-nums">
                        <span className="text-bank-ink font-medium">{st.approved_count}</span>
                        <span className="text-bank-muted ml-1 text-[10px]">({st.approval_rate}%)</span>
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono text-[11px] tabular-nums">
                        <span className={cn(st.duplicate_count > 0 ? "text-bank-dup-ink font-medium" : "text-bank-muted")}>
                          {st.duplicate_count}
                        </span>
                        <span className="text-bank-muted ml-1 text-[10px]">({st.duplicate_rate}%)</span>
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono text-[11px] tabular-nums">
                        <span className={cn(st.rejected_count > 0 ? "text-bank-bad-ink font-medium" : "text-bank-muted")}>
                          {st.rejected_count}
                        </span>
                        <span className="text-bank-muted ml-1 text-[10px]">({st.rejection_rate}%)</span>
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono text-[11px] tabular-nums">
                        {st.pending_review_count + st.stale_count > 0 ? (
                          <span className="bg-bank-attention-wash text-bank-attention-ink rounded px-1.5 py-0.5 font-medium">
                            {st.pending_review_count + st.stale_count}
                          </span>
                        ) : (
                          <span className="text-bank-muted">0</span>
                        )}
                      </td>
                      <td className="min-w-[210px] px-3 py-2">
                        <StackDifficultyBar item={st} />
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono text-[11px] tabular-nums">
                        {formatScore(st.avg_quality_score)}
                      </td>
                      <td className="w-12 px-2 py-2.5 text-center">
                        <HealthChip status={st.health_status} />
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {canExport && (
                            <button
                              type="button"
                              disabled={st.approved_count === 0 || exportingStack === st.stack_path}
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                void handleExport(st.stack_path);
                              }}
                              title={
                                st.approved_count > 0
                                  ? `Export ${st.approved_count} approved questions as DOCX`
                                  : "No approved questions to export"
                              }
                              className={cn(
                                "rounded p-1 text-bank-muted transition-colors",
                                st.approved_count > 0
                                  ? "hover:bg-bank-surface-3 hover:text-bank-accent-ink"
                                  : "cursor-not-allowed opacity-25",
                              )}
                            >
                              {exportingStack === st.stack_path ? (
                                <Loader2 className="size-3.5 animate-spin text-bank-accent" />
                              ) : (
                                <FileDown className="size-3.5" />
                              )}
                            </button>
                          )}
                          <ChevronRight className="text-bank-muted inline size-4" />
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </Panel>

      <StackExportModal
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        items={data?.items ?? []}
        initialSelected={exportModalInitial}
      />
    </div>
  );
}
